<script setup>
import { WdsButtonGhost, WdsEventCard } from '@wds/components';

const props = defineProps({
  eventList: {
    type: Array,
    required: true,
  },
  cityName: {
    type: String,
    default: '',
  },
  locationData: {
    type: Object,
    default: null,
  },
  viewMoreUrl: {
    type: String,
    default: '',
  },
});

// ### COMPOSABLES
const { $tracking } = useNuxtApp();
const {
  t,
  locale,
} = useI18n();
const { useUrl } = useUrls();
const { useFormatter } = useFormatters();

// ### COMPUTED
const hasValidEvents = computed(() => {
  return Array.isArray(props.eventList) && props.eventList.length > 0;
});

const eventListForCards = computed(() => {
  if (!Array.isArray(props.eventList)) {
    return [];
  }

  return props.eventList.map((event) => {
    const eventPicture = event?.picture?.mobile || '';
    const eventLocation = event?.locationName || '';

    const eventLink = useUrl.getInternalEventPageUrl({
      rootSlug: t('t_OGEVENTSSLUG'),
      eventId: event.eventId,
      eventNameSlug: useFormatter.normalizeUrl(event.name),
    });

    return {
      id: event.eventId,
      bind: {
        language: locale.value,
        name: event.name,
        href: eventLink,
        startDate: event.dates?.startDate,
        endDate: event.dates?.endDate,
        category: event.eventType,
        picture: eventPicture,
        location: eventLocation,
      },
      tracking: {
        id: event.eventId,
        name: event.name,
        startDate: event.dates?.startDate,
        endDate: event.dates?.endDate,
        city: event?.location?.city?.name ?? event?.location?.cityName ?? props.cityName ?? '',
        country: event?.location?.country?.name
          ?? event?.location?.countryName
          ?? props.locationData?.country?.name
          ?? '',
      },
    };
  });
});

const displayCityName = computed(() => {
  if (props.cityName) {
    return props.cityName;
  }

  const firstLocation = props.eventList?.[0]?.location;
  return firstLocation?.city?.name || firstLocation?.cityName || '';
});

const viewMoreHref = computed(() => {
  if (props.viewMoreUrl) {
    return props.viewMoreUrl;
  }

  const eventLocation = props.locationData || props.eventList?.[0]?.location;

  if (!eventLocation) {
    return '';
  }

  const urlFriendlyContinent = eventLocation?.continent?.urlFriendlyName;
  const urlFriendlyCountry = eventLocation?.country?.urlFriendlyName;
  const urlFriendlyCity = eventLocation?.city?.urlFriendlyName;

  if (!urlFriendlyContinent || !urlFriendlyCountry || !urlFriendlyCity) {
    return '';
  }

  return useUrl.getEventsCityPageUrl({
    rootSlug: t('t_OGEVENTSSLUG'),
    urlFriendlyContinent,
    urlFriendlyCountry,
    urlFriendlyCity,
  });
});

function handleMoveItem() {
  $tracking?.onTrackEvent({ action: 'Occasion Genius Carousel Click' });
}

function handleViewMoreClick() {
  $tracking?.onTrackEvent({ action: 'Occasion Genius Explore Click' });
}

function handleEventCardClick(event) {
  $tracking?.onTrackEvent({
    action: 'Occasion Genius Event Click',
    occasion_genius_event_id: event?.id || '',
    occasion_genius_event_name: event?.name || '',
    occasion_genius_event_city: event?.city || '',
    occasion_genius_event_country: event?.country || '',
    occasion_genius_event_start_date: event?.startDate || '',
    occasion_genius_event_end_date: event?.endDate || event?.startDate || '',
  });
}
</script>

<template>
  <section v-if="hasValidEvents" class="events-in-city-banner-container">
    <h2 class="events-in-city-banner-title">
      <i18n-t keypath="t_OGEVENTEVENTSINLOCATION">
        <template #LOCATIONNAME>
          <span>{{ displayCityName }}</span>
        </template>
      </i18n-t>
    </h2>
    <CommonHwCarousel
      class="events-in-city-banner-carousel"
      :fade-boundaries="true"
      :item-count="eventList.length"
      @move-item="handleMoveItem"
    >
      <WdsEventCard
        v-for="event in eventListForCards"
        :key="event.id"
        class="events-in-city-banner-card"
        orientation="horizontal"
        v-bind="event.bind"
        @click="handleEventCardClick(event.tracking)"
      />
    </CommonHwCarousel>
    <WdsButtonGhost
      class="events-in-city-banner-view-more"
      :text="$t('t_VIEWMORE')"
      icon-end="arrow-right"
      :href="viewMoreHref"
      @click="handleViewMoreClick"
    />
  </section>
</template>

<style lang="scss" scoped>
.events-in-city-banner-container {
  display: flex;
  flex-direction: column;
  gap: $wds-spacing-m;

  .events-in-city-banner-title {
    @include title-3-bld;

    overflow-wrap: break-word;

    :deep(span) {
      color: $wds-color-purple;
    }
  }

  .events-in-city-banner-carousel {
    :deep(.carousel-gallery-list) {
      gap: wds-rem(12px);
    }
  }

  .events-in-city-banner-card {
    width: stretch;
  }

  .events-in-city-banner-view-more {
    margin-left: auto;
  }

  @include tablet-large {
    .events-in-city-banner-title {
      @include title-2-bld;
    }
  }
}
</style>
